import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { UserRole } from '../models/user.model';
import { ToastService } from '../../shared/services/toast.service';

/**
 * Guarda de rota por lista explícita de papéis.
 *
 * Antes recebia um papel mínimo e comparava por nível numérico. Como AGENT e SERVICE_MANAGER
 * empatavam nesse nível, tudo liberado para o corretor liberava para o gestor predial junto —
 * era a raiz do vazamento. Agora a rota declara exatamente quem entra.
 *
 * ROOT e ADMIN passam sempre, espelhando o bypass do backend.
 */
export function roleGuard(allowedRoles: readonly UserRole[]): CanActivateFn {
  return () => {
    const authService = inject(AuthService);
    const router = inject(Router);

    if (!authService.isAuthenticated()) {
      router.navigate(['/login']);
      return false;
    }

    if (authService.hasAnyRole(allowedRoles)) {
      return true;
    }

    // Sem permissão: volta para a tela que todo papel enxerga.
    router.navigate(['/overview']);
    return false;
  };
}

/**
 * Trava de rota por aba: entra o administrador e o membro que tem QUALQUER uma das abas.
 * Quem não tem volta para a Visão Geral com um aviso — inclusive quem abriu o link direto.
 * A API recusa os mesmos dados por conta própria (`@Tabs` nas rotas): isto aqui é a tela.
 */
export function tabGuard(...tabs: readonly string[]): CanActivateFn {
  return () => {
    const authService = inject(AuthService);
    const router = inject(Router);

    if (!authService.isAuthenticated()) {
      return router.parseUrl('/login');
    }

    if (authService.hasAnyTab(tabs)) {
      return true;
    }

    inject(ToastService).warn(
      'Você não tem acesso a essa aba',
      'Peça a um administrador para liberar em Usuários.',
    );
    return router.parseUrl('/overview');
  };
}
